/**
 * What a page becomes when something under it throws.
 *
 * `ARCHITECTURE.md` §Frontend asks for "a route-level error boundary per portal; a
 * `problem+json` renderer that shows the `title` and the `code`, nothing more", and
 * `.docs/08-web-foundation.md` §11 added the `req_id` line and a *Try again*.
 * R1-25 is what that turned out to look like from the other side of the screen: a
 * paying client on `/app/reports/2099-01` was shown `NotFound`, a UUID, and a
 * button that would fail identically for ever. **Nothing more** was read as "the
 * code, then", and the code is a token for an agent to branch on rather than a
 * sentence for a person. The rule survives; what satisfies it does not.
 *
 * So the boundary distinguishes the cases honestly, because they want different
 * things from the reader. One card that says all three badly is what R1-25 was.
 *
 * | what threw | the card | the way forward |
 * |---|---|---|
 * | `401` | "your session has ended" | `/login`, once, keeping where you were |
 * | `403` **with a problem body** | "You do not have access to this" + the API's sentence | back to the portal |
 * | `404` **with a problem body** | `<NotFound>` — the card an unmatched address gets | back to the portal |
 * | a `4xx` refusal with a body (`409`, `429`, …) | its `title` + its `detail` | try again |
 * | `5xx`, or any answer that was not a problem body | "Something went wrong" | try again |
 * | a `NetworkError` | "Cannot reach the portal" | try again |
 * | anything else — a bug in a component | "Something went wrong" | try again |
 *
 * A `5xx` deliberately does **not** show the API's own detail, whatever it says.
 * `http/problem.ts` `problemOfUnknown` used to write "…Quote the instance id when
 * you get in touch" — a sentence asking the reader for the one value this boundary
 * has stopped printing — and session 24's F6 fixed that sentence at the source. The
 * rule here is unchanged and is not about that sentence: a 500 detail is written for
 * whoever reads the log, and this card is written for whoever is looking at the
 * screen. Ours asks them to try, then tell us; the instance id is on the section as
 * `data-request-id` and in the console line below, which is `ErrorCard`'s own note
 * on why.
 *
 * What must never appear is anything the server did not choose to publish: a
 * stack, a query, a driver message. `http/problem.ts` strips those server-side,
 * and an unexpected client-side throw is rendered as a fixed sentence rather than
 * as `error.message`, which in a bundle is minified nonsense at best and a leaked
 * value at worst.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { useAuth } from '../auth/provider.js'
import { invalidateSession, sessionQueryOptions } from '../auth/session.js'
import { isApiProblem, isNetworkError, isUnauthenticated, type ApiProblem } from '../api/problem.js'
import { BackToPortal, ErrorCard, TryAgain } from './ErrorCard.js'
import { NotFound } from './NotFound.js'

export type RouteErrorProps = {
  readonly error: unknown
  /** TanStack Router's own reset. Clears the boundary and re-runs the route. */
  readonly reset?: () => void
}

/** The one sentence for a failure nobody can be told anything useful about. */
const WENT_WRONG = 'This page could not be shown. Trying again is worth one go; then tell us.'

/** What a refusal says when the API published no sentence of its own. */
const NO_ACCESS = 'This is not on your account, or your role does not include it.'

export function RouteError({ error, reset }: RouteErrorProps) {
  useConsoleTrace(error)

  if (isUnauthenticated(error)) return <SessionExpired />

  if (isNetworkError(error)) {
    return (
      <ErrorCard
        title="Cannot reach the portal"
        detail="The request did not get an answer. Check your connection and try again."
      >
        {reset === undefined ? undefined : <TryAgain reset={reset} />}
      </ErrorCard>
    )
  }

  if (isApiProblem(error)) return <ProblemCard problem={error} reset={reset} />

  return (
    <ErrorCard title="Something went wrong" detail={WENT_WRONG}>
      {reset === undefined ? undefined : <TryAgain reset={reset} />}
    </ErrorCard>
  )
}

/**
 * The table above, as three questions in the order they stop being true.
 *
 * `401` never arrives here — `RouteError` answers it first — so the two statuses
 * left with a *way back* rather than a *retry* are the two a retry cannot help:
 * the thing is not there, or it is not yours. Everything else might be a moment.
 *
 * **`fromBody` guards both of them**, and it is not a detail. A `404` that the API
 * *wrote* is a report that does not exist; a `404` that arrived without a problem
 * body is a path nobody mounted — nginx answering for a route that is not there, or
 * `/api/auth/get-session` missing off a half-finished deploy. Telling a client
 * "there is nothing here, go back to the portal" about the second one would be the
 * portal agreeing with a deployment fault. That is a failure on our side, and it
 * gets our sentence and a retry, like every other one.
 */
function ProblemCard({
  problem,
  reset,
}: {
  readonly problem: ApiProblem
  readonly reset?: (() => void) | undefined
}) {
  /*
   * Nothing here was written for the reader: a `5xx` — whose own detail asks them to
   * quote an instance id this boundary no longer prints — or an answer that was not
   * a problem body at all.
   */
  const ours = problem.status >= 500 || !problem.fromBody

  if (!ours && problem.status === 404) {
    return <NotFound detail={problem.detail} requestId={problem.requestId} />
  }

  if (!ours && problem.status === 403) {
    return (
      <ErrorCard
        cap="403"
        title="You do not have access to this"
        detail={sentenceOf(problem, NO_ACCESS)}
        requestId={problem.requestId}
      >
        <BackToPortal />
      </ErrorCard>
    )
  }

  /*
   * A refusal the API wrote a sentence for — an allowance exceeded, an invoice that
   * is not payable, a rate limit. `problem.title` is derived from the code by
   * `problemTitle` and reads as English ("Allowance exceeded"), and `detail` is the
   * sentence somebody can act on.
   */
  return (
    <ErrorCard
      title={ours ? 'Something went wrong' : problem.title}
      detail={ours ? WENT_WRONG : sentenceOf(problem, WENT_WRONG)}
      requestId={problem.requestId}
    >
      {reset === undefined ? undefined : <TryAgain reset={reset} />}
    </ErrorCard>
  )
}

/**
 * The sentence the API wrote, or ours.
 *
 * `detail` is optional in RFC 9457 and `ApiProblem` accepts an empty one, and an
 * empty `<p>` under a heading is a card that looks broken rather than one that
 * says something. `http/problem.ts` writes `problemTitle(code) + '.'` in that case
 * today, so this is the guard for the day it does not.
 */
function sentenceOf(problem: ApiProblem, fallback: string): string {
  return problem.detail === '' ? fallback : problem.detail
}

/**
 * The request id, where a developer looks for it.
 *
 * One line per boundary render, carrying the code, the status and the `instance`.
 * `ErrorCard`'s header says why it is not on the screen; this is the other half of
 * that trade — a support request still starts with one string, and a bug report
 * that includes the console already includes it.
 */
function useConsoleTrace(error: unknown): void {
  useEffect(() => {
    if (!isApiProblem(error)) return
    const id = error.requestId === null ? 'none' : error.requestId
    console.error(`[portal] ${error.code} ${String(error.status)} req_id ${id}`, error)
  }, [error])
}

/**
 * A session that ended mid-visit.
 *
 * `.docs/08-web-foundation.md` §Edge cases: "the next query returns `401` → the
 * boundary redirects to `/login?redirect=…` **once, not in a loop**". Three things
 * make that true, and the third is the one that actually closes the loop:
 *
 *   1. **The session cache is dropped and re-read first.** Otherwise `/login`'s
 *      own guard still believes there is a session and sends you back.
 *   2. **The effect runs once per mount, and never on `/login`.** A boundary that
 *      re-rendered would otherwise navigate again on every render.
 *   3. **The redirect only happens if the fresh read agrees you are signed out.**
 *      If the API says 401 while Better Auth still says you are somebody, the two
 *      disagree — and bouncing between `/app` and `/login` for ever is the worst
 *      possible way to say so. That state gets a card instead, which is also what a
 *      person can act on: sign out and back in.
 *
 * The re-read can now fail rather than answer, which is the point of R1-02: a `502`
 * on `get-session` is not "signed out". `queryClient.query` rejects in that case,
 * and the catch below stops on the disagreement card rather than sending somebody
 * with a live cookie to a sign-in screen they are throttled out of.
 */
function SessionExpired() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const auth = useAuth()
  const href = useRouterState({ select: (state) => state.location.href })
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const sent = useRef(false)
  const [disagree, setDisagree] = useState(false)

  useEffect(() => {
    if (sent.current || pathname === '/login') return
    sent.current = true
    void (async () => {
      await invalidateSession(queryClient)
      try {
        const refreshed = await queryClient.query(sessionQueryOptions(auth))
        if (refreshed.principal.kind !== 'anonymous') {
          setDisagree(true)
          return
        }
      } catch {
        setDisagree(true)
        return
      }
      await navigate({ to: '/login', search: { redirect: href }, replace: true })
    })()
  }, [auth, navigate, queryClient, href, pathname])

  if (disagree) {
    return (
      <ErrorCard
        title="Still signed in"
        detail="This page was refused, but your session has not ended. Sign out and back in."
      />
    )
  }

  return (
    <section className="stack" role="status" aria-live="polite">
      <p className="prose">Your session has ended. Taking you back to sign in…</p>
    </section>
  )
}
